import { fromDate } from './flexDate.js';
import { airtableValueToFlexDate } from './airtableFlexDate.js';

/**
 * The "Created" field — ONE name for one idea, at every layer.
 *
 *   canonical field key   `created`               (public.fields, value_storage 'derived')
 *   display name          "Created"
 *   view configs persist  'created'               (calendar.primaryDateField, gallery.groupField…)
 *   read it with          createdDate(node)       — or createdFlexDate for Calendar/Timeline
 *   guard a field ref     isCreatedField(ref)
 *
 * If you are adding a surface, use those names verbatim. Earlier drafts of this
 * work also called the field `__created__` and `effective_created_date`, and the
 * reader `effectiveCreatedDate`. All three are retired: "effective" described the
 * fallback rule rather than the field, and a rule is not a name.
 *
 * ── What the value is ──────────────────────────────────────────────────────
 *
 * Two stored columns back it, and they answer different questions:
 *
 *   source_created_date  (node.sourceCreatedDate)  When the thing was created at
 *     its SOURCE — Obsidian frontmatter, Bubble's Created Date, a message's
 *     sent-at. Written by importers and ingest. NULL means born in Arcos, or the
 *     source had no date. Shown as "Source Created Date", and the field a person
 *     edits to correct an imported item's date.
 *
 *   created_at  (node.createdTime)  When the ROW entered Arcos. Audit column: the
 *     database stamps it, and neither the app nor any importer writes it. Shown as
 *     "Date Added". NOTE the node property is still `createdTime` — Airtable-era
 *     naming, shared with notes and other tables, so it kept its name; this is the
 *     one place that maps it to "Date Added".
 *
 * Created is the coalesce: the source date when we know it, else created_at. For a
 * natively-created item the two are the same moment by construction, which is why
 * the everyday UI only ever needs to show this one. It is computed at read time and
 * deliberately not stored — a NULL source date is information (it means "born
 * here"), and stamping every native create would erase it.
 *
 * Every UNLABELED "Created" surface must read this: the standard Date Created sort,
 * the Grid's Created column, card metas, the item sidebar. Reading created_at
 * directly is correct ONLY where the surface is explicitly about arrival in Arcos
 * (DataView's raw column, a future "Date Added" option) and says so. Sorting a
 * mixed imported-and-native space by raw created_at is the bug this exists to kill:
 * an item the sidebar dates to 2019 sorting as if created on import day.
 *
 * Values are ISO-8601 strings (timestamptz via PostgREST; legacy jsonb values are
 * ISO-ish), so lexicographic order is chronological — comparators can localeCompare
 * what this returns.
 *
 * @param {{ sourceCreatedDate?: string|null, createdTime?: string|null }} node
 * @returns {string|null} ISO date string, or null when the node has neither.
 */
export function createdDate(node) {
  return node?.sourceCreatedDate || node?.createdTime || null;
}

/* ── The field, for pickers that enumerate field definitions ────────────────
 *
 * Sort and the Grid's Created column can call createdDate directly, but the
 * field-DRIVEN pickers (Calendar / Timeline date axis, Pivot + Kanban axes,
 * group-by, gallery body fields) enumerate field DEFINITIONS — and a computed
 * value has no definition to find. Before this existed they offered only
 * 'Source Created Date', which is blank on every natively-created item, so
 * charting by it silently dropped half a mixed space.
 *
 * So we hand them one, exactly as the Pivot lane builder already synthesizes Item
 * Display Type. It is registered in the canonical field library (key `created`,
 * value_storage 'derived') so the Fields tab tells the truth about it, but it is
 * deliberately NOT in ALL_INTERNAL_FIELDS / getPickableInternalFields: that set
 * also feeds field CREATION (SetupWizard, SettingsModal), the type-schema editor,
 * the Grid's column builder, and the sort dropdown — none of which should see a
 * field with no column behind it, and the last two would show a second "Created"
 * beside the built-in one. The two server paths that turn `fields` rows into
 * picker options filter to space-scoped rows AND value_storage in
 * ('column','jsonb'), so the canonical row can't double up here either.
 *
 * READ-ONLY, and it must stay that way: splitItemFields maps no column for this
 * key, so a write would land in the custom_fields jsonb and shadow the real dates
 * from then on. Two things hold that line — the type is 'createdTime', which every
 * read-only gate in the app already knows (the Grid's READ_ONLY_TYPES,
 * SlotInlineCreate's READ_ONLY_DATE_TYPES), and the Calendar / Timeline drag,
 * resize, and tray-schedule handlers bail on isCreatedField before writing.
 */

/** The identity: the canonical `fields` key, and what view configs persist. */
export const CREATED_FIELD_KEY = 'created';

/** The display name, matching the canonical row's display_name. */
export const CREATED_FIELD_LABEL = 'Created';

/**
 * Picker-shaped descriptor. `id`/`name` are the identity and `label` is what people
 * read — the same split every custom field uses, so FieldListPicker and the group
 * pickers render it correctly without special-casing.
 */
export const CREATED_FIELD_DEF = Object.freeze({
  id: CREATED_FIELD_KEY,
  name: CREATED_FIELD_KEY,
  label: CREATED_FIELD_LABEL,
  type: 'createdTime',
  source: 'internal',
  synthetic: true,
  readOnly: true,
});

/**
 * True when a persisted field reference points at the Created field.
 *
 * Matches the key ONLY. A space can have a jsonb field whose display name is
 * "Created", and hijacking that would be worse than the problem this solves.
 *
 * @param {string|null|undefined} fieldRef  id or name from a view config
 */
export function isCreatedField(fieldRef) {
  return fieldRef === CREATED_FIELD_KEY;
}

/**
 * The node's Created date as a FlexDate, for the Calendar / Timeline readers.
 *
 * Delegates to `airtableValueToFlexDate`, which every other date field already goes
 * through, because a date-only value needs different handling than an instant:
 * "2019-03-01" is a wall-clock calendar day, and parsing it as UTC midnight then
 * reading local fields lands it on Feb 28 for every viewer west of UTC. Obsidian
 * frontmatter dates arrive in exactly that shape.
 *
 * The one widening: that parser's datetime pattern allows at most milliseconds,
 * while PostgREST hands back timestamptz with microseconds
 * (Example: "2019-03-01T12:34:56.123456+00:00"), which would parse to null and drop the
 * item from the view. So trim sub-millisecond digits first, and keep a Date-based
 * fallback for any other datetime shape a legacy jsonb value holds.
 *
 * @returns {{year:number,month:number,day:number,hour?:number,minute?:number}|null}
 */
export function createdFlexDate(node) {
  const raw = createdDate(node);
  if (!raw) return null;
  if (raw instanceof Date) {
    return isNaN(raw.getTime()) ? null : fromDate(raw, { withTime: true });
  }
  if (typeof raw !== 'string') return null;

  const fd = airtableValueToFlexDate(raw.replace(/(\.\d{3})\d+/, '$1'));
  if (fd) return fd;

  const d = new Date(raw);
  return isNaN(d.getTime()) ? null : fromDate(d, { withTime: true });
}
